<template>
  <div class="price-input-rub">
    <label class="price-input-rub__label">
      <span v-if="title">{{ title }}</span>
      <input
        ref="inputPrice"
        class="price-input-rub__input"
        type="text"
        inputmode="numeric"
        aria-label="Поле ввода цены в тенге"
        v-bind:maxlength="maxLength"
        v-bind:disabled="disabled"
        v-bind:value="formattedValue"
        v-on:input="onInput"
      />
    </label>
    <p
      v-for="(error, index) in violations"
      v-bind:key="index"
      role="alert"
      class="price-input-rub__violations">{{ error }}</p>
  </div>
</template>

<script>

/**
 * @description Компонент поля ввода стоимости в рублях.
 */
export default {
  data() {
    return {
      /**
       * @description Значение поля ввода.
       * @type {string}
       */
      inputValue: (this.value / 100).toString(),
    };
  },
  computed: {
    /**
     * @description Форматированное значение для отображения.
     * @returns {string}
     */
    formattedValue() {
      return this.format(this.round(this.value));
    },
  },
  methods: {
    /**
     * @description Округлить значение.
     * @param {number} value
     * @returns {string}
     */
    round(value) {
      const round = Math.round(value || 0);
      return (round / 100).toFixed(2);
    },
    /**
     * @description Форматировать значение.
     * @param {string} value
     * @returns {string}
     */
    format(value) {
      const parts = value.toString().split('.');
      const rub = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
      if (this.withoutKopeck) {
        return rub;
      }
      const kop = parts[1];
      if (kop === '00') {
        return rub;
      }
      return [rub, kop].join('.');
    },
    /**
     * @description Обработать ввод.
     * @param {InputEvent} event
     * @returns {void}
     */
    onInput(event) {
      const { value } = event.target;
      if (value === '') {
        this.$emit('input', this.clamp(0));
        return;
      }
      const rub = this.withoutKopeck
        ? this.parseRubles(value)
        : this.parseValue(value);
      const kop = this.max !== undefined
        ? Math.min(rub * 100, this.max)
        : rub * 100;
      this.inputValue = (kop / 100).toString();
      this.$emit('input', this.clamp(kop));
    },
    /**
     * @description Ограничить значение диапазоном от min до max включительно.
     * @param {number} initialValue
     * @returns {number}
     */
    clamp(initialValue) {
      let value = initialValue;
      if (this.max !== undefined) {
        value = Math.min(value, this.max);
      }
      if (this.min !== undefined) {
        value = Math.max(value, this.min);
      }
      return value;
    },
    /**
     * @description Получить значение.
     * @param {string} value
     * @returns {number}
     */
    parseValue(value) {
      const regex = /[^\d.]/gmu;
      const price = value.replace(regex, '');
      const format = /^[\d]{0,10}(\.[\d]{0,2})?/g;
      const result = format.exec(price);
      if (result !== null) {
        return +result[0];
      }
      return 0;
    },
    /**
     * @description Получить значение в рублях.
     * @param {string} value
     * @returns {number}
     */
    parseRubles(value) {
      const regex = /[^\d.]/gmu;
      const price = value.replace(regex, '');
      const format = /^[\d]{0,10}/g;
      const result = format.exec(price);
      if (result !== null) {
        return +result[0];
      }
      return 0;
    },
  },
  props: {
    title: { default: '', type: String },
    maxLength: { default: null, type: Number, required: false },
    disabled: { default: false, type: Boolean },
    max: { default: () => undefined, type: Number },
    min: { default: () => undefined, type: Number },
    value: { required: true, type: Number },
    violations: { default: () => [], type: Array },
    withoutKopeck: { default: false, type: Boolean },
  },
};
</script>

<style lang="scss" scoped>
.price-input-rub {
  &__label {
    display: grid;
    row-gap: 2px;
    > span {
      font-size: 12px;
      font-weight: 400;
      line-height: 16px;
      color: var(--unfocus-color);
    }
  }
  &__input {
    width: 100%;
    height: 40px;
    padding: 10px;
    padding-right: 32px;
    font-size: 16px;
    background: no-repeat right 10px center;
    background-image: url("@/assets/img/switch/tenge-grey.svg");
    background-size: 18px 18px;
    border-radius: var(--input-border-radius);
    border: 1px solid var(--border-color);
    &:focus-visible {
      outline-color: var(--action-color);
    }
  }
  &__violations {
    padding: 0.125rem;
    font-size: 12px;
    font-weight: 400;
    line-height: 14px;
    color: var(--warning-color);
  }
}
</style>
